import React, { useEffect, useState } from 'react';
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';

import { MainContent, PageHeading } from '../../components/Shell/Content';
import { getUserFullName } from '../../components/helpers/functions';
import LoadingSkeleton from '../../components/Shell/LoadingSkeleton';
import BetaAttributes, { configurePayload } from '../../components/Attributes';
import {
	TemplateBoolean,
	TemplateDropdown,
} from '../../components/Attributes/Attributes_old';
import Modal from '../../components/Shell/Modal';
import Button from '../../components/Button';

import useRequired from '../../hooks/useRequired';
import useAsync from '../../hooks/useAsync';

import { addService, getService, updateService } from '../../api/services';
import { getModuleFieldsBeta } from '../../api/modules';

const NOTIFY_OPTIONS = [
	{ label: 'When generated', value: 0 },
	{ label: '3 days', value: 3 },
	{ label: '1 week', value: 7 },
	{ label: '2 weeks', value: 14 },
	{ label: '30 days', value: 30 },
	{ label: '60 days', value: 60 },
	{ label: 'Do not notify', value: -1 },
];

const SERVICE_MODULE_ID = process.env.REACT_APP_MODULE_ID_SERVICE;

const DEFAULT_NOTIFICATION_FORM = {
	change: false,
	time: -1,
	renotify: -1,
	//admins: false,
	powerusers: true,
};

const ServiceForm = () => {
	const navigate = useNavigate();

	const { id } = useParams();

	const [form, setForm] = useState({});
	const [updatedForm, setUpdateForm] = useState({});
	const [notificationForm, setNotificationForm] = useState(
		DEFAULT_NOTIFICATION_FORM
	);

	const [notificationModal, setNotificationModal] = useState(false);

	const { execute: onAddService } = useAsync(addService, { immediate: false });

	const { value: service, execute: onRefreshService } = useAsync(getService, {
		immediate: !!id,
		...(id
			? {
					defaultPayload: {
						id,
					},
			  }
			: {}),
	});

	const { execute: onUpdateService } = useAsync(updateService, {
		immediate: false,
	});

	const {
		value: moduleFields,
		status: isLoadingModuleFields,
		execute: onRefreshModuleFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: SERVICE_MODULE_ID, active: true },
	});

	useEffect(() => {
		const start = updatedForm?.serviceStart?.value;
		const occurrences = updatedForm?.occurrences?.value;

		if (start && !occurrences) {
			if (moment(start).diff(moment(), 'days') <= 7) {
				setNotificationForm({ ...notificationForm, time: 0 });
			} else if (moment(start).diff(moment(), 'days') <= 30) {
				setNotificationForm({ ...notificationForm, time: 7 });
			} else {
				setNotificationForm({ ...notificationForm, time: 30 });
			}
		} else if (start && occurrences) {
			if (occurrences === 'daily') {
				setNotificationForm({ ...notificationForm, time: 0 });
			} else if (occurrences === 'weekly' || occurrences === 'biweekly') {
				setNotificationForm({ ...notificationForm, time: 7 });
			} else {
				setNotificationForm({ ...notificationForm, time: 30 });
			}
		} else {
			setNotificationForm(DEFAULT_NOTIFICATION_FORM);
		}
	}, [updatedForm?.serviceStart?.value, updatedForm?.occurrences?.value]);

	useEffect(() => {
		const categoryId =
			updatedForm?.category?.value?.value || form?.category?.value?.value;

		if (categoryId) {
			onRefreshModuleFields(
				{
					id: SERVICE_MODULE_ID,
					active: true,
					categoryId,
				},
				null,
				{ silent: true }
			);
		}
	}, [updatedForm?.category?.value?.value, form?.category?.value?.value]);

	useEffect(() => {
		if (service && moduleFields) {
			// 1. On update attempt, create update object
			let update = {};

			const CONVERTER = {
				serviceStart: 'startDate',
				serviceVendor: 'contacts',
				category: 'categoryId',
			};

			// Update update object with event fields
			moduleFields?.combined?.map((field) => {
				const key = CONVERTER[field.key] || field?.key;
				if (service[key]) {
					update[field?.key] = {
						fieldId: field?.id,
						value: service[key],
					};
				}
			});

			// Custom functions()

			if (service?.contacts?.length) {
				update['vendor'] = {
					value: service?.contacts[0]?.id,
				};
			}

			if (update?.assets?.value?.length) {
				update['assets'] = {
					...update.assets,
					value: update?.assets?.value?.map((asset) => ({
						asset,
						label: `${asset?.manufacturer?.name} ${asset?.model?.name}`,
						value: asset?.id,
					})),
				};
			}

			if (id && update.serviceStart) {
				update['serviceStart'] = {
					...update?.serviceStart,
					value: moment(update?.serviceStart?.value).add(1, 'Y').format(),
				};
			}

			if (id && update?.serviceVendor?.value?.length) {
				const vendor = update?.serviceVendor?.value[0];
				update['serviceVendor'] = {
					...update?.serviceVendor,
					value: { label: getUserFullName(vendor), value: vendor?.id },
				};
			}

			if (
				id &&
				service?.notificationSettings &&
				Object.keys(service?.notificationSettings).length > 0
			) {
				setNotificationForm({
					...service?.notificationSettings,
					change: false,
				});
			}

			setForm({ ...update });
		}
	}, [service, moduleFields]);

	const hasAllRequired = useRequired(
		{ ...form, ...updatedForm },
		moduleFields?.combined
			?.filter((field) => field.isRequired)
			.map((field) => field.key)
		/*() => {
			const isChecklist = form['category']
				? form['category']?.value?.label === 'Checklist'
				: false;

			return isChecklist ? form['checklist']?.value?.length : true;
			//return form['checklist'] ? : isChecklist;
		}*/
	);

	const skipModuleKeys = [...(id ? ['serviceStart'] : [])];

	return (
		<>
			<div className="flex flex-1">
				<MainContent maxWidth="w-full xl:w-1/2">
					<div>
						<PageHeading
							divider={false}
							actions={[
								{
									name: <BellAlertIcon className="w-4 h-4" />,
									type: 'secondary',
									onClick: () => setNotificationModal(true),
								},
								{
									name: id ? 'Update task' : 'Create task',
									onClick: () => {
										if (id) {
											onUpdateService(
												{
													id,
													...configurePayload(
														{ ...updatedForm },
														moduleFields?.combined
													),
													...(notificationForm?.change
														? { notificationSettings: notificationForm }
														: {}),
													/*...(updatedForm?.checklist
														? { checklist: updatedForm?.checklist?.value }
														: {}),*/
												},
												(response) =>
													navigate(`/manage/tasks/${response?.id}`)
											);
										} else {
											const onSubmit = (payload) =>
												onAddService(payload, (response) =>
													navigate(`/manage/tasks/${response?.id}`)
												);

											if (
												notificationForm?.change ||
												!updatedForm?.serviceStart?.value
											) {
												onSubmit({
													...configurePayload(
														updatedForm,
														moduleFields?.combined
													),
													notificationSettings: { ...notificationForm },
												});
											} else {
												setNotificationModal({
													onSubmit: (payload) => {
														onSubmit(payload);
													},
												});
											}
										}
									},
									disabled: !hasAllRequired,
								},
							]}
							onBack={() => history.goBack()}
						>
							{id ? `Update Task: ${service?.name}` : 'Create a new task'}
						</PageHeading>

						<div className="m-auto mb-6">
							{isLoadingModuleFields === 'pending' ? (
								<LoadingSkeleton />
							) : (
								<>
									<div className="grid grid-cols-2 gap-y-4 gap-x-4 text-left">
										{moduleFields?.standard
											?.filter(
												(field) => skipModuleKeys.indexOf(field.key) === -1
											)
											?.map((field) => (
												<BetaAttributes
													field={field}
													onChange={(index, value, fullValue) => {
														if (value || fullValue) {
															let updatedValue = form[index]?.value;

															if (fullValue) {
																updatedValue = fullValue;
															} else {
																if (updatedValue && Array.isArray(value)) {
																	updatedValue = [
																		...(Array.isArray(updatedValue)
																			? updatedValue
																			: [updatedValue]),
																		...value,
																	];
																} else {
																	updatedValue = value;
																}
															}

															setUpdateForm({
																...updatedForm,
																[index]: {
																	value: updatedValue,
																	fieldId: field.id,
																},
															});
														} else {
															let updated = { ...updatedForm };
															updated[index] = {
																...updated[index],
																value: null,
															};

															if (index === 'category') {
																delete updated['checklist'];
															}

															setUpdateForm(updated);
														}
													}}
													form={{ ...form, ...updatedForm }}
													updatedForm={updatedForm}
													setForm={setUpdateForm}
													moduleId={SERVICE_MODULE_ID}
												/>
											))}
									</div>
								</>
							)}
						</div>
					</div>
				</MainContent>
			</div>

			<Modal
				title="Notification Settings"
				open={!!notificationModal}
				onClose={() => setNotificationModal(false)}
				size="2xl"
			>
				{updatedForm?.serviceStart?.value || form?.serviceStart?.value ? (
					<>
						<dl className="mt-6 space-y-2 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6">
							<div className="pt-2 sm:flex">
								<dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 flex  mt-2  text-xs text-gray-400">
									Notify time
								</dt>
								<dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
									<div className="text-gray-900">
										<TemplateDropdown
											className={'w-72'}
											options={NOTIFY_OPTIONS}
											value={notificationForm?.time}
											onChange={(index, value) =>
												setNotificationForm({
													...notificationForm,
													time: value,
													change: true,
												})
											}
										/>
									</div>
								</dd>
							</div>
							<div className="pt-2 sm:flex">
								<dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 flex  mt-2  text-xs text-gray-400">
									Re-notify after every
								</dt>
								<dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
									<div className="text-gray-900">
										<TemplateDropdown
											className={'w-72'}
											options={NOTIFY_OPTIONS}
											value={notificationForm?.renotify}
											onChange={(index, value) =>
												setNotificationForm({
													...notificationForm,
													renotify: value,
													change: true,
												})
											}
										/>
									</div>
								</dd>
							</div>

							{/*<div className="pt-2 sm:flex">
								<dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 flex  mt-2  text-xs text-gray-400">
									Notify admins
								</dt>
								<dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
									<div className="text-gray-900">
										<TemplateBoolean
											value={notificationForm?.admins}
											description={'Enable/disable notifications for admins'}
											onChange={(index, value) =>
												setNotificationForm({
													...notificationForm,
													admins: value,
													change: true,
												})
											}
										/>
									</div>
								</dd>
										</div>*/}

							<div className="pt-2 sm:flex">
								<dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6 flex  mt-2  text-xs text-gray-400">
									Notify asset power users
								</dt>
								<dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
									<div className="text-gray-900">
										<TemplateBoolean
											value={notificationForm?.powerusers}
											description={
												'Enable/disable notifications for power users'
											}
											onChange={(index, value) =>
												setNotificationForm({
													...notificationForm,
													powerusers: value,
													change: true,
												})
											}
										/>
									</div>
								</dd>
							</div>
						</dl>
						<div className="flex justify-end mt-3">
							<Button
								onClick={() => {
									if (typeof notificationModal?.onSubmit === 'function') {
										notificationModal?.onSubmit({
											...configurePayload(updatedForm, moduleFields?.combined),
											notificationSettings: {
												...notificationForm,
												change: true,
											},
										});
									} else {
										setNotificationForm({ ...notificationForm, change: true });
										setNotificationModal(false);
									}
								}}
							>
								{typeof notificationModal?.onSubmit === 'function'
									? id
										? 'Update task'
										: 'Create task'
									: id
									? 'Close'
									: 'Close'}
							</Button>
						</div>
					</>
				) : (
					<>
						<div className="text-xs text-gray-500">
							Please select a start date to enable notifications
						</div>
						<div className="flex justify-end mt-3">
							<Button onClick={() => setNotificationModal(false)}>Close</Button>
						</div>
					</>
				)}
			</Modal>
		</>
	);
};

export default ServiceForm;
